/**
 * "Choose a new password" — shown in place of the whole app while GET /me says must_change_password (a manager's
 * reset or the founder's bootstrap left this session on a temporary password). Every other session route answers
 * 403 must_change_password until POST /auth/password/change succeeds, so there is nothing else to show; Sign out
 * still works. On success /me is read again and the person lands on their home route.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { PASSWORD_MIN_LENGTH, type MeResponse } from '@amplispot/contracts';
import { call } from '../api';
import { ME_KEY } from '../lib/session';
import { checkNewPassword, passwordChangeError, type NewPasswordErrors } from '../lib/password';
import { SignInBrand } from './SignIn';
import { useSignOut } from '../shell/sign-out';

export function ChangePassword({ me }: { me: MeResponse }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errs, setErrs] = useState<NewPasswordErrors & { current_password?: string }>({});
  const [formErr, setFormErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = 'Choose a new password · Amplispot Ops';
    return () => {
      document.title = 'Amplispot Ops';
    };
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormErr(null);
    const local = { ...checkNewPassword(current, next, confirm), ...(current ? {} : { current_password: 'Enter the temporary password you signed in with.' }) };
    setErrs(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      await call('auth.passwordChange', { body: { current_password: current, new_password: next } });
      // The gate lifts once /me says must_change_password: false.
      const fresh = await qc.fetchQuery({ queryKey: ME_KEY, queryFn: () => call('me.get'), staleTime: 0 });
      void qc.invalidateQueries();
      nav(fresh.home_route, { replace: true });
    } catch (x) {
      const r = passwordChangeError(x);
      if (r.field) setErrs({ [r.field]: r.message });
      else setFormErr(r.message);
    } finally {
      setBusy(false);
    }
  };

  // Shared with the top bar: leaves only once the server ended the session (or 401); otherwise stays and toasts.
  const { signOut, signingOut } = useSignOut();

  return (
    <div className="signin">
      <div className="signin-wrap">
        <SignInBrand />
        <main className="signin-card">
          <div>
            <h1>Choose a new password</h1>
            <p className="lead">
              {me.user.name.split(' ')[0]}, you signed in with a temporary password. Choose your own before you carry on. It replaces the temporary one, and
              signs you out on your other devices.
            </p>
          </div>
          {formErr ? (
            <div className="note bad" role="alert">
              {formErr}
            </div>
          ) : null}
          <form onSubmit={submit} style={{ display: 'grid', gap: 12 }} noValidate>
            <label className="fld" style={{ marginBottom: 0 }}>
              <span>Temporary password</span>
              <input
                className="in"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                aria-invalid={errs.current_password ? true : undefined}
                autoFocus
              />
              {errs.current_password ? <small className="ferr">{errs.current_password}</small> : null}
            </label>
            <label className="fld" style={{ marginBottom: 0 }}>
              <span>New password</span>
              <input
                className="in"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                aria-invalid={errs.new_password ? true : undefined}
              />
              {errs.new_password ? <small className="ferr">{errs.new_password}</small> : <small className="fhint">At least {PASSWORD_MIN_LENGTH} characters.</small>}
            </label>
            <label className="fld" style={{ marginBottom: 0 }}>
              <span>New password again</span>
              <input
                className="in"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                aria-invalid={errs.confirm ? true : undefined}
              />
              {errs.confirm ? <small className="ferr">{errs.confirm}</small> : null}
            </label>
            <button className="btn pri" type="submit" disabled={busy} style={{ justifyContent: 'center' }}>
              {busy ? 'Saving…' : 'Save new password'}
            </button>
          </form>
          <p className="fine">
            Signed in as {me.user.email}.{' '}
            <button type="button" className="lnk" onClick={signOut} disabled={signingOut} aria-busy={signingOut}>
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </p>
        </main>
      </div>
    </div>
  );
}
